/**
 * Widget Registry — Data-Driven Dashboard Section Rendering (DRR-321)
 *
 * Widgets self-register from their packages. Dashboard sections resolve
 * widgets by `component_hint` lookup from constellation YAML.
 *
 * @example
 * // In subgraph-fhir/src/widgets/index.ts
 * registerWidget({
 *   componentHint: 'fhir-care-plan',
 *   component: FhirCarePlanWidget,
 *   package: 'fhir',
 * });
 *
 * // In SubjectDashboardPage.tsx
 * const Widget = getWidget(section.componentHint)?.component;
 * if (Widget) return <Widget {...props} />;
 */

import type { ComponentType } from 'react';

/**
 * Standard props passed to all dashboard widgets.
 * Widgets can destructure only what they need.
 */
export interface WidgetProps {
  /** Actor ID of the subject being viewed */
  subjectActorId: string;
  /** Tenant slug (e.g., "hillside-harmony") */
  tenantId: string;
  /** Tenant number for DDB queries */
  tenantNum?: number;
  /** Navigation callback for deep links */
  onNavigate?: (path: string) => void;
  /** Callback when user clicks a journey/workflow */
  onJourneyClick?: (instanceId: string) => void;
}

/**
 * Widget definition for registry.
 */
export interface WidgetDefinition {
  /** kebab-case identifier matching constellation YAML component_hint */
  componentHint: string;
  /** React component to render */
  component: ComponentType<WidgetProps>;
  /** Source package: 'core', 'fhir', 'erp', 'payroll' */
  package: string;
  /** Human-readable label for dev tools */
  label?: string;
  /** Widget category for grouping */
  category?: 'clinical' | 'operational' | 'financial' | 'communication' | 'reporting';
}

// Internal registry map
const WIDGET_REGISTRY = new Map<string, WidgetDefinition>();

/**
 * Register a widget component.
 * Call this from package barrel exports (e.g., subgraph-fhir/src/widgets/index.ts).
 *
 * @example
 * registerWidget({
 *   componentHint: 'fhir-medication-list',
 *   component: FhirMedicationListWidget,
 *   package: 'fhir',
 *   category: 'clinical',
 * });
 */
export function registerWidget(def: WidgetDefinition): void {
  if (WIDGET_REGISTRY.has(def.componentHint)) {
    console.warn(
      `[WidgetRegistry] Widget "${def.componentHint}" already registered by package "${WIDGET_REGISTRY.get(def.componentHint)?.package}". ` +
        `Overwriting with "${def.package}".`
    );
  }
  WIDGET_REGISTRY.set(def.componentHint, def);
}

/**
 * Get a widget definition by component_hint.
 * Returns undefined if not registered.
 */
export function getWidget(componentHint: string): WidgetDefinition | undefined {
  return WIDGET_REGISTRY.get(componentHint);
}

/**
 * Check if a widget is registered.
 */
export function hasWidget(componentHint: string): boolean {
  return WIDGET_REGISTRY.has(componentHint);
}

/**
 * List all registered widgets.
 * Useful for dev tools and debugging.
 */
export function listWidgets(): WidgetDefinition[] {
  return Array.from(WIDGET_REGISTRY.values());
}

/**
 * List widgets by package.
 */
export function listWidgetsByPackage(pkg: string): WidgetDefinition[] {
  return listWidgets().filter((w) => w.package === pkg);
}

/**
 * List widgets by category.
 */
export function listWidgetsByCategory(category: WidgetDefinition['category']): WidgetDefinition[] {
  return listWidgets().filter((w) => w.category === category);
}

/**
 * Get registry stats for debugging.
 */
export function getRegistryStats(): {
  total: number;
  byPackage: Record<string, number>;
  byCategory: Record<string, number>;
} {
  const widgets = listWidgets();
  const byPackage: Record<string, number> = {};
  const byCategory: Record<string, number> = {};

  for (const w of widgets) {
    byPackage[w.package] = (byPackage[w.package] ?? 0) + 1;
    if (w.category) {
      byCategory[w.category] = (byCategory[w.category] ?? 0) + 1;
    }
  }

  return { total: widgets.length, byPackage, byCategory };
}
